/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useState } from 'react';
import api, { AxiosError } from '@/services/api';
import { States } from '@/features/registerEvent/components/SectorTicketMainSettingsSreen/screens/ui';
import PaymentGateway from '@/model/PaymentGateway';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import { toast } from 'react-toastify';
import { useDialog } from '@/hooks/useDialog';
import { unmask } from '@/helpers/masks/cashNumber';
import { useParams } from 'react-router-dom';
import DiscountCoupon from '@/model/DiscountCoupon';
import {
  FormInputName as FormInputNameToPaymentSettings,
  SectorTicketPaymentSettingsContainer,
  ShouldShowModal,
} from '@/features/registerEvent/components/SectorTicketPaymentSettingScreen/screens/ui';
import {
  SectorTicketContainerProps,
  TabSectorTicketActionsProps,
} from '@/features/registerEvent/screens/SectorTicket/ui';
import { convertToBoolean } from '@/helpers/common/convertToBoolean';
import { toPercentage } from '@/helpers/common/amount';
import CompanyParameter from '@/model/CompanyParameter';
import { REACT_APP_USER } from '@/utils/config';
import { getItem } from '@/helpers/common/localStorage';
import { AuthUser } from '@/model/AuthUser';
import TicketPayment from '@/model/TicketPayment';
import { formPaymentSettingsProps, PaymentSettingsActionsProps } from '../types';

export default interface PayloadSectorTicketPaymentSettings {
  websiteGateway: string;
  websiteInstallmentLimit: number;
  posInstallmentLimit: number;
  allowFractionalPayment: boolean;
  allowVariableValue: boolean;
  allowPaymentBankSlip: boolean;
  allowPaymentPIX: boolean;
  allowSellingWebsite: boolean;
  allowSellingPos: boolean;
  printReceipt: boolean;
  physicalSale: {
    id: string;
    allowCreditCardPayment: boolean;
    debit: number;
    credit: number;
    pix: number;
    bankSlip: number;
    administrateTax: number;
    installments: number;
    fee: number;
  };
  websiteSale: {
    id: string;
    allowCreditCardPaymen: boolean;
    debit: number;
    credit: number;
    pix: number;
    bankSlip: number;
    administrateTax: number;
    installments: number;
    fee: number;
  };
  allowDiscount: boolean;
  allowDiscountCoupon: boolean;
  discountCoupons: [
    {
      id: string;
    },
  ];
}

type UrlParams = {
  id: string;
};

export const SectorTicketPaymentSettingsScreen: React.FC<
  Pick<SectorTicketContainerProps, 'ticketStates'> &
    Pick<SectorTicketContainerProps, 'ticketStep'> &
    Omit<TabSectorTicketActionsProps, 'reloadTickets'>
> = ({ ticketStates, ticketStep, nextTab, backTab }): JSX.Element => {
  const [state, setState] = useState<States>(States.default);
  const [enableFee, setEnableFee] = useState<boolean>(false);
  const [paymentGatewayList, setPaymentGatewayList] = useState<PaymentGateway[]>([]);
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(
    ShouldShowModal.discountCoupons,
  );
  const [discountCoupon, setDiscountCoupon] = useState<DiscountCoupon[]>([]);
  const [listDiscountCoupon, setListDiscountCoupon] = useState<DiscountCoupon[]>([]);

  const params = useParams<UrlParams>();
  const { title, visible, onChangeTitle, onToggle } = useDialog();

  const {
    formData: formDataPaymentSettings,
    formErrors: formErrorsPaymentSettings,
    onChangeFormInput: onChangeFormInputPaymentSettings,
    isFormValid: isFormValidtPaymentSettings,
    resetForm: resetFormPaymentSettings,
  } = useForm({
    initialData: {
      allowSellingWebsite: 'true',
      allowSellingPos: 'true',
      allowPaymentCredit: 'true',
      creditWebsiteGateway: '',
      creditPosGateway: '',
      allowPaymentDebit: 'true',
      debitWebsiteGateway: '',
      debitPosGateway: '',
      allowPaymentPIX: 'true',
      pixWebsiteGateway: '',
      pixPosGateway: '',
      allowPaymentBankSlip: 'false',
      bankslipWebsiteGateway: '',
      bankslipPosGateway: '',
      allowFractionalPayment: 'true',
      printReceipt: 'true',
      physicalSaleId: '',
      physicalSaleDebit: '',
      physicalSaleCredit: '',
      physicalSalePix: '',
      physicalSaleAdministrateTax: '',
      physicalSaleInstallments: '',
      physicalSaleFee: '',
      websiteSaleId: '',
      websiteSaleDebit: '',
      websiteSaleCredit: '',
      websiteSalePix: '',
      websiteSaleAdministrateTax: '',
      websiteSaleInstallments: '',
      websiteSaleFee: '',
      allowDiscountCoupon: 'false',
    },
    validators: {
      allowSellingWebsite: [validators.required],
      allowSellingPos: [validators.required],
      allowPaymentCredit: [validators.required],
      allowPaymentDebit: [validators.required],
      allowPaymentPIX: [validators.required],
      allowPaymentBankSlip: [validators.required],
      allowFractionalPayment: [validators.required],
      printReceipt: [validators.required],
      physicalSaleDebit: [validators.required, validators.between(0.1, 99.99)],
      physicalSaleCredit: [validators.required, validators.between(1, 99.99)],
      physicalSalePix: [validators.required, validators.between(1, 99.999)],
      physicalSaleAdministrateTax: [validators.required, validators.between(1, 99.99)],
      physicalSaleInstallments: [validators.required, validators.between(0, 24)],
      physicalSaleFee: [validators.required, validators.between(1, 99.99)],
      websiteSaleBankSlip: [validators.required, validators.between(0.1, 99.99)],
      websiteSaleCredit: [validators.required, validators.between(1, 99.99)],
      websiteSalePix: [validators.required, validators.between(1, 99.99)],
      websiteSaleAdministrateTax: [validators.required, validators.between(1, 99.99)],
      websiteSaleInstallments: [validators.required, validators.between(0, 24)],
      websiteSaleFee: [validators.required, validators.between(1, 99.99)],
      allowDiscountCoupon: [validators.required],
    },
    formatters: {},
  });

  const controllerFormPaymentSettings: formPaymentSettingsProps = {
    formData: formDataPaymentSettings,
    formErrors: formErrorsPaymentSettings,
    onChangeFormInput: onChangeFormInputPaymentSettings,
  };

  const handleOnCheckAlterFeePermission = (): void => {
    let found = false;
    const user = getItem(String(REACT_APP_USER)) as AuthUser;
    if (user && user.roles && user.roles.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < user.roles.length; i++) {
        if (user.roles[i] === 'Adm-Alter-Fee-Event') {
          found = true;
          break;
        }
      }
    }
    setEnableFee(found);
  };

  const getDefaultTicketFee = async (): Promise<void> => {
    const { ticket } = ticketStates;
    if (!ticket || !ticket.payment) {
      try {
        setState(States.loading);
        const { data } = await api.get<CompanyParameter>(`/company/parameter/fee/${params.id}`);
        if (data.ticketPhysicalSale) {
          onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSaleDebit)(
            toPercentage(data.ticketPhysicalSale.debit),
          );
          onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSaleCredit)(
            toPercentage(data.ticketPhysicalSale.credit),
          );
          onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSalePix)(
            toPercentage(data.ticketPhysicalSale.pix),
          );
          onChangeFormInputPaymentSettings(
            FormInputNameToPaymentSettings.physicalSaleAdministrateTax,
          )(toPercentage(data.ticketPhysicalSale.administrateTax));
          onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSaleInstallments)(
            String(data.ticketPhysicalSale.installments),
          );
          onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSaleFee)(
            toPercentage(data.ticketPhysicalSale.fee),
          );
        }
        if (data.ticketWebsiteSale) {
          onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSaleBankSlip)(
            toPercentage(data.ticketWebsiteSale.bankSlip),
          );
          onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSaleCredit)(
            toPercentage(data.ticketWebsiteSale.credit),
          );
          onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSalePix)(
            toPercentage(data.ticketWebsiteSale.pix),
          );
          onChangeFormInputPaymentSettings(
            FormInputNameToPaymentSettings.websiteSaleAdministrateTax,
          )(toPercentage(data.ticketWebsiteSale.administrateTax));
          onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSaleInstallments)(
            String(data.ticketWebsiteSale.installments),
          );
          onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSaleFee)(
            toPercentage(data.ticketWebsiteSale.fee),
          );
        }
      } finally {
        setState(States.default);
      }
    }
  };

  const handleAddDiscountCoupon = (): void => {
    setDiscountCoupon([
      ...discountCoupon,
      {
        id: '',
        name: '',
        code: '',
        amount: null,
        discountType: 0,
        discount: null,
      },
    ]);
  };

  const handleChangeDiscountCoupon = (inputName: string, index: number, value: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newFormValues = [...discountCoupon] as any;
    newFormValues[index][inputName] = value;
    setDiscountCoupon(newFormValues);
  };

  const handleRemoveDiscountCoupon = (index: number): void => {
    const values = [...discountCoupon];
    values.splice(index, 1);
    setDiscountCoupon(values);
    setListDiscountCoupon(values);
  };

  const handleFecthGateway = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<PaymentGateway[]>('/payment-gateway/find');
      setPaymentGatewayList(data);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnDiscountCoupon = async (): Promise<void> => {
    try {
      // verify if the bank account not exists values empty
      const discountCouponEmpty = discountCoupon.find(
        item =>
          item.name === '' || item.amount === null || item.code === '' || item.discount === null,
      );
      if (discountCouponEmpty) {
        toast.warn(
          'Preencha todos os campos ou remova o cupom de desconto que contém campos vazios',
        );
        return;
      }
      /*

      // sum total amount of discount coupon
      const totalAmountDiscountCoupon = discountCoupon.reduce(
        (acc, cupom) => acc + (cupom.amount as number),
        0,
      );
      // sum total amount of tickets
      const totalAmountTickets = ticketStates.ticket?.batchs.reduce(
        (acc, batch) => acc + batch.amount,
        0,
      ) as number;

      // verify if the total amount of discount coupon is greater than the total amount of the event
      if (totalAmountDiscountCoupon > totalAmountTickets) {
        toast.warn(
          'A quantidade de cupons digitadas é maior do que a quantidade total de ingressos cadastrados',
        );
        return;
      }
        */

      setListDiscountCoupon(discountCoupon);
      onToggle();
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    }
  };

  const handleOnSaveSectorTicketPayment = async ({
    isBntNext,
  }: {
    isBntNext: boolean;
  }): Promise<void> => {
    try {
      setState(States.loading);
      if (isFormValidtPaymentSettings()) {
        const payloadDiscountCoupon = listDiscountCoupon.map(item => ({
          id: item.id,
          name: item.name,
          code: item.code,
          amount: item.amount,
          discountType: item.discountType,
          discount: item.discount ? +unmask(String(item.discount || 0)) : 0,
        }));

        const payload = {
          eventTickets: [
            {
              id: ticketStep.ticketState?.id,
            },
          ],
          allowSellingWebsite: convertToBoolean(
            formDataPaymentSettings[FormInputNameToPaymentSettings.allowSellingWebsite],
          ),
          allowSellingPos: convertToBoolean(
            formDataPaymentSettings[FormInputNameToPaymentSettings.allowSellingPos],
          ),
          allowPaymentCredit: convertToBoolean(
            formDataPaymentSettings[FormInputNameToPaymentSettings.allowPaymentCredit],
          ),
          allowPaymentDebit: convertToBoolean(
            formDataPaymentSettings[FormInputNameToPaymentSettings.allowPaymentDebit],
          ),
          allowPaymentPIX: convertToBoolean(
            formDataPaymentSettings[FormInputNameToPaymentSettings.allowPaymentPIX],
          ),
          allowPaymentBankSlip: convertToBoolean(
            formDataPaymentSettings[FormInputNameToPaymentSettings.allowPaymentBankSlip],
          ),
          allowFractionalPayment: convertToBoolean(
            formDataPaymentSettings[FormInputNameToPaymentSettings.allowFractionalPayment],
          ),
          printReceipt: convertToBoolean(
            formDataPaymentSettings[FormInputNameToPaymentSettings.printReceipt],
          ),
          physicalSale: {
            id: formDataPaymentSettings[FormInputNameToPaymentSettings.physicalSaleId]
              ? formDataPaymentSettings[FormInputNameToPaymentSettings.physicalSaleId]
              : (undefined as unknown as string),
            debit: +unmask(
              formDataPaymentSettings[FormInputNameToPaymentSettings.physicalSaleDebit],
            ),
            bankSlip: 0,
            credit: +unmask(
              formDataPaymentSettings[FormInputNameToPaymentSettings.physicalSaleCredit],
            ),
            pix: +unmask(formDataPaymentSettings[FormInputNameToPaymentSettings.physicalSalePix]),
            administrateTax: +unmask(
              formDataPaymentSettings[FormInputNameToPaymentSettings.physicalSaleAdministrateTax],
            ),
            installments:
              +formDataPaymentSettings[FormInputNameToPaymentSettings.physicalSaleInstallments],
            fee: +unmask(formDataPaymentSettings[FormInputNameToPaymentSettings.physicalSaleFee]),
          },
          websiteSale: {
            id: formDataPaymentSettings[FormInputNameToPaymentSettings.websiteSaleId]
              ? formDataPaymentSettings[FormInputNameToPaymentSettings.websiteSaleId]
              : (undefined as unknown as string),
            debit: undefined as unknown as number,
            credit: +unmask(
              formDataPaymentSettings[FormInputNameToPaymentSettings.websiteSaleCredit],
            ),
            bankSlip: +unmask(
              formDataPaymentSettings[FormInputNameToPaymentSettings.websiteSaleBankSlip],
            ),

            pix: +unmask(formDataPaymentSettings[FormInputNameToPaymentSettings.websiteSalePix]),
            administrateTax: +unmask(
              formDataPaymentSettings[FormInputNameToPaymentSettings.websiteSaleAdministrateTax],
            ),
            installments: +unmask(
              formDataPaymentSettings[FormInputNameToPaymentSettings.websiteSaleInstallments],
            ),
            fee: +unmask(formDataPaymentSettings[FormInputNameToPaymentSettings.websiteSaleFee]),
          },
          allowDiscountCoupon: convertToBoolean(
            formDataPaymentSettings[FormInputNameToPaymentSettings.allowDiscountCoupon],
          ),
          discountCoupons: payloadDiscountCoupon,
        } as TicketPayment;

        if (
          ticketStep &&
          ticketStep.ticketState &&
          ticketStep.ticketState.payment &&
          ticketStep.ticketState.payment.id
        ) {
          payload.id = ticketStep.ticketState.payment.id;
        }

        if (payload.allowPaymentCredit) {
          if (
            payload.allowSellingWebsite &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.creditWebsiteGateway] &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.creditWebsiteGateway].trim()
              .length > 0
          ) {
            payload.creditWebsiteGateway = {
              id: formDataPaymentSettings[FormInputNameToPaymentSettings.creditWebsiteGateway],
            } as PaymentGateway;
          }
          if (
            payload.allowSellingPos &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.creditPosGateway] &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.creditPosGateway].trim().length >
              0
          ) {
            payload.creditPosGateway = {
              id: formDataPaymentSettings[FormInputNameToPaymentSettings.creditPosGateway],
            } as PaymentGateway;
          }
        }

        if (payload.allowPaymentDebit) {
          if (
            payload.allowSellingWebsite &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.debitWebsiteGateway] &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.debitWebsiteGateway].trim()
              .length > 0
          ) {
            payload.debitWebsiteGateway = {
              id: formDataPaymentSettings[FormInputNameToPaymentSettings.debitWebsiteGateway],
            } as PaymentGateway;
          }
          if (
            payload.allowSellingPos &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.debitPosGateway] &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.debitPosGateway].trim().length >
              0
          ) {
            payload.debitPosGateway = {
              id: formDataPaymentSettings[FormInputNameToPaymentSettings.debitPosGateway],
            } as PaymentGateway;
          }
        }

        if (payload.allowPaymentPIX) {
          if (
            payload.allowSellingWebsite &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.pixWebsiteGateway] &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.pixWebsiteGateway].trim()
              .length > 0
          ) {
            payload.pixWebsiteGateway = {
              id: formDataPaymentSettings[FormInputNameToPaymentSettings.pixWebsiteGateway],
            } as PaymentGateway;
          }
          if (
            payload.allowSellingPos &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.pixPosGateway] &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.pixPosGateway].trim().length > 0
          ) {
            payload.pixPosGateway = {
              id: formDataPaymentSettings[FormInputNameToPaymentSettings.pixPosGateway],
            } as PaymentGateway;
          }
        }

        if (payload.allowPaymentBankSlip) {
          if (
            payload.allowSellingWebsite &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.bankslipWebsiteGateway] &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.bankslipWebsiteGateway].trim()
              .length > 0
          ) {
            payload.bankslipWebsiteGateway = {
              id: formDataPaymentSettings[FormInputNameToPaymentSettings.bankslipWebsiteGateway],
            } as PaymentGateway;
          }
          if (
            payload.allowSellingPos &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.bankslipPosGateway] &&
            formDataPaymentSettings[FormInputNameToPaymentSettings.bankslipPosGateway].trim()
              .length > 0
          ) {
            payload.bankslipPosGateway = {
              id: formDataPaymentSettings[FormInputNameToPaymentSettings.bankslipPosGateway],
            } as PaymentGateway;
          }
        }

        const response = await api.post(`/event/ticket/${params.id}/payment`, payload);

        if (response && isBntNext) nextTab();
        if (response) toast.success('Dados salvos com sucesso!');
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleBackTab = (): void => {
    backTab();
  };

  const controllerPaymentSettingsActions: PaymentSettingsActionsProps = {
    onSave: () => handleOnSaveSectorTicketPayment({ isBntNext: false }),
    onReturnTab: handleBackTab,
    onNextTap: () => handleOnSaveSectorTicketPayment({ isBntNext: true }),
  };

  const handleOnShouldShowModal = ({
    value,
    newTitleModal,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event?: Event | any;
  }): void => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    onToggle();
  };

  useEffect(() => {
    const { ticket } = ticketStates;

    if (ticket?.payment) {
      resetFormPaymentSettings();
      onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.allowSellingWebsite)(
        String(ticket.payment.allowSellingWebsite),
      );
      onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.allowSellingPos)(
        String(ticket.payment.allowSellingPos),
      );
      onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.allowPaymentCredit)(
        String(ticket.payment.allowPaymentCredit),
      );
      if (ticket.payment.creditWebsiteGateway) {
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.creditWebsiteGateway)(
          ticket.payment.creditWebsiteGateway.id,
        );
      }
      if (ticket.payment.creditPosGateway) {
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.creditPosGateway)(
          ticket.payment.creditPosGateway.id,
        );
      }
      onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.allowPaymentDebit)(
        String(ticket.payment.allowPaymentDebit),
      );
      if (ticket.payment.debitWebsiteGateway) {
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.debitWebsiteGateway)(
          ticket.payment.debitWebsiteGateway.id,
        );
      }
      if (ticket.payment.debitPosGateway) {
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.debitPosGateway)(
          ticket.payment.debitPosGateway.id,
        );
      }
      onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.allowPaymentPIX)(
        String(ticket.payment.allowPaymentPIX),
      );
      if (ticket.payment.pixWebsiteGateway) {
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.pixWebsiteGateway)(
          ticket.payment.pixWebsiteGateway.id,
        );
      }
      if (ticket.payment.pixPosGateway) {
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.pixPosGateway)(
          ticket.payment.pixPosGateway.id,
        );
      }
      onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.allowPaymentBankSlip)(
        String(ticket.payment.allowPaymentBankSlip),
      );
      if (ticket.payment.bankslipWebsiteGateway) {
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.bankslipWebsiteGateway)(
          ticket.payment.bankslipWebsiteGateway.id,
        );
      }
      if (ticket.payment.bankslipPosGateway) {
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.bankslipPosGateway)(
          ticket.payment.bankslipPosGateway.id,
        );
      }
      onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.allowFractionalPayment)(
        String(ticket.payment.allowFractionalPayment),
      );
      onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.printReceipt)(
        String(ticket.payment.printReceipt),
      );
      if (ticket.payment.physicalSale) {
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSaleId)(
          ticket.payment.physicalSale.id,
        );
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSaleDebit)(
          toPercentage(ticket.payment.physicalSale.debit),
        );
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSaleCredit)(
          toPercentage(ticket.payment.physicalSale.credit),
        );
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSalePix)(
          toPercentage(ticket.payment.physicalSale.pix),
        );
        onChangeFormInputPaymentSettings(
          FormInputNameToPaymentSettings.physicalSaleAdministrateTax,
        )(toPercentage(ticket.payment.physicalSale.administrateTax));
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSaleInstallments)(
          String(ticket.payment.physicalSale.installments),
        );
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.physicalSaleFee)(
          toPercentage(ticket.payment.physicalSale.fee),
        );
      }

      if (ticket.payment.websiteSale) {
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSaleId)(
          ticket.payment.websiteSale.id,
        );

        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSaleBankSlip)(
          toPercentage(ticket.payment.websiteSale.bankSlip),
        );
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSaleCredit)(
          toPercentage(ticket.payment.websiteSale.credit),
        );
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSalePix)(
          toPercentage(ticket.payment.websiteSale.pix),
        );
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSaleAdministrateTax)(
          toPercentage(ticket.payment.websiteSale.administrateTax),
        );
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSaleInstallments)(
          String(ticket.payment.websiteSale.installments),
        );
        onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.websiteSaleFee)(
          toPercentage(ticket.payment.websiteSale.fee),
        );
      }

      onChangeFormInputPaymentSettings(FormInputNameToPaymentSettings.allowDiscountCoupon)(
        String(ticket.payment.allowDiscountCoupon),
      );
      setDiscountCoupon(ticket.payment?.discountCoupons || []);
      setListDiscountCoupon(ticket.payment?.discountCoupons || []);
    }
  }, [ticketStates.ticket]);

  useEffect(() => {
    handleOnCheckAlterFeePermission();
    getDefaultTicketFee();
    handleFecthGateway();
  }, []);

  return (
    <SectorTicketPaymentSettingsContainer
      state={state}
      enableFee={enableFee}
      controllerFormPaymentSettings={controllerFormPaymentSettings}
      paymentGatewayList={paymentGatewayList}
      onShouldShowModal={handleOnShouldShowModal}
      title={title}
      shouldShowModal={shouldShowModal}
      visible={visible}
      onToggle={onToggle}
      handleAddDiscountCoupon={handleAddDiscountCoupon}
      discountCoupon={discountCoupon}
      handleChangeDiscountCoupon={handleChangeDiscountCoupon}
      handleRemoveDiscountCoupon={handleRemoveDiscountCoupon}
      listDiscountCoupon={listDiscountCoupon}
      handleOnDiscountCoupon={handleOnDiscountCoupon}
      ticketStates={ticketStates}
      backTab={backTab}
      nextTab={nextTab}
      paymentSettingsActions={controllerPaymentSettingsActions}
    />
  );
};
